import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  // Send the httpOnly session cookie (DEF-23) alongside requests so the backend
  // can authenticate via cookie. The Bearer header below stays as a transitional
  // fallback until all call sites are migrated off localStorage.
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Auth now rides on the httpOnly session cookie (withCredentials:true above).
// We no longer read the JWT from localStorage — that closed the DEF-23 XSS
// token-theft vector. Any remaining `Bearer null` headers from legacy call
// sites are ignored by the backend, which falls back to the cookie.

// Handle auth errors with silent token refresh
// On 401, attempt one refresh-token call and retry the original request
// before kicking the user out. Eliminates the "logged off mid-task" pain
// when the JWT expires while the user is still actively using the app.
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeToRefresh = (cb) => { refreshSubscribers.push(cb); };
const notifyRefreshSubscribers = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

// Global access-denied (403) feedback (P1-7). Debounced so a burst of 403s on
// a page load can't spam the user with duplicate toasts.
let _lastForbiddenToast = 0;
const notifyForbiddenOnce = (msg) => {
  const now = Date.now();
  if (now - _lastForbiddenToast < 3000) return;
  _lastForbiddenToast = now;
  try { toast.error(msg); } catch { /* toast is optional */ }
};

// Public routes have NO session by design (employee self-serve onboarding,
// public document signing, public forms, auth pages). A background 401 from
// the app's /auth/me bootstrap must NOT hijack these pages to /login, or the
// links we text/email to employees & clients would be unusable.
const PUBLIC_PATH_PREFIXES = [
  "/login", "/pcw-login", "/pcw/onboard/", "/sign/", "/form/",
  "/forgot-password", "/reset-password", "/register", "/403", "/error/",
];
const isPublicPath = () => {
  const p = window.location.pathname || "";
  return PUBLIC_PATH_PREFIXES.some((pre) => p === pre || p.startsWith(pre));
};

// Send the user to the login screen with a clear reason AND a returnTo so that
// after they sign back in they land exactly where they were (not the home
// page). The Login page shows a friendly "your session expired" toast for the
// reason param. Guarded so we never loop on public/login pages.
const redirectToLogin = (reason) => {
  if (window.location.pathname === "/login" || isPublicPath()) return;
  const here = `${window.location.pathname}${window.location.search}` || "/";
  const params = new URLSearchParams({ reason });
  if (here && here !== "/") params.set("returnTo", here);
  window.location.href = `/login?${params.toString()}`;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const url = originalRequest.url || "";

    // Global access-denied (403) pattern (P1-7): a 403 means the user lacks
    // permission for THIS action — it is NEVER a session problem, so we never
    // refresh the token or log out. Give consistent feedback when the user
    // took an ACTION (a mutation), but stay silent on GET probes so
    // permission-aware widgets can hide quietly without a scary toast.
    // Components can opt out with `{ suppressForbiddenToast: true }`.
    if (status === 403) {
      const m = (originalRequest.method || "get").toLowerCase();
      if (!originalRequest.suppressForbiddenToast && m !== "get") {
        const d403 = (error.response?.data?.detail || "").toString();
        notifyForbiddenOnce(d403 || "You don't have permission to do that.");
      }
      return Promise.reject(error);
    }

    // Forced-logout cutover: when an admin ends everyone's sessions, the
    // backend replies 401 "Session was ended by an administrator". Detect
    // that exact case and route to a FRIENDLY security-update notice — no
    // point attempting a token refresh (it'd fail the same way).
    const detail = (error.response?.data?.detail || "").toString();
    if (
      status === 401 &&
      /ended by an administrator/i.test(detail) &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/register")
    ) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login?reason=security_logout";
      }
      return Promise.reject(error);
    }

    // Step-up re-auth (owner password confirmation on destructive SMS ops):
    // a 401 here means "wrong password for THIS action", NOT an expired
    // session. Let the calling component show its inline error — never
    // refresh the token or redirect to /login (that would log the owner
    // out and unmount the password modal before the error renders).
    const method = (originalRequest.method || "").toLowerCase();
    const isStepUp =
      url.includes("/sms/archived/purge") ||
      (method === "delete" && (url.includes("/sms/messages/") || url.includes("/sms/threads/")));
    if (status === 401 && isStepUp) {
      return Promise.reject(error);
    }

    // Hard-skip refresh logic for endpoints that should never be retried
    const skipRefresh =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/2fa/verify-login") ||
      url.includes("/2fa/verify-recovery") ||
      url.includes("/refresh-token") ||
      originalRequest._retried;

    if (status !== 401 || skipRefresh) {
      // Original behaviour for unrecoverable 401s — log out and redirect.
      //
      // BUT NOT for the login/register endpoints themselves — those 401s
      // mean "wrong credentials", not "session expired", and the page
      // already has a toast handler that shows the right error. A hard
      // window.location reload here would destroy that toast and leave
      // the user staring at a silent re-render of the login form.
      // (June 2026 — Dawn typed her wrong password 21 times before we
      // realised the interceptor was eating the error.)
      const isLoginFlow =
        url.includes("/auth/login") ||
        url.includes("/auth/register");
      if (
        status === 401 &&
        skipRefresh &&
        !isLoginFlow &&
        !url.includes("/2fa/verify-login") &&
        !url.includes("/2fa/verify-recovery") &&
        !url.includes("/refresh-token")
      ) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        if (!isPublicPath()) {
          redirectToLogin("session_ended");
        }
      }
      return Promise.reject(error);
    }

    // 401 → try a single silent refresh, then retry the original request
    originalRequest._retried = true;

    if (isRefreshing) {
      // A refresh is already in flight — queue this request and wait
      return new Promise((resolve, reject) => {
        subscribeToRefresh((ok) => {
          if (!ok) return reject(error);
          resolve(api(originalRequest));
        });
      });
    }

    isRefreshing = true;
    try {
      // Refresh mints a new session; the backend re-sets the httpOnly cookie
      // (DEF-23). We no longer persist the JWT in JS, so just retry the call.
      await api.post("/auth/refresh-token");
      notifyRefreshSubscribers(true);
      return api(originalRequest);
    } catch (refreshErr) {
      notifyRefreshSubscribers(null);
      // Only tear down the session on a DEFINITIVE auth rejection from the
      // server (refresh returned 401/403). A transient network error — no
      // response object at all: offline blip, VPN/proxy drop, DNS failure,
      // CORS preflight, gateway 502/503/504 — must NOT destroy a valid
      // session, otherwise a flaky connection logs the user out every few
      // minutes even though their token is perfectly good.
      const refreshStatus = refreshErr?.response?.status;
      if (refreshErr?.response && (refreshStatus === 401 || refreshStatus === 403)) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        if (window.location.pathname !== "/login" && !isPublicPath()) {
          redirectToLogin("session_ended");
        }
      }
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

// Auth API (invite-only registration)
export const aiGovernanceAPI = {
  baaStatus: () => api.get("/ai/baa-status"),
  attestBaa: (payload) => api.post("/ai/baa-attest", payload),
  phiLlmAudit: (params) => api.get("/ai/phi-llm-audit", { params }),
  exportPhiLlmAudit: (params) => api.get("/ai/phi-llm-audit/export", { params, responseType: "blob" }),
};

export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  pcwLogin: (data) => api.post("/auth/pcw/login", data),
  getMe: () => api.get("/auth/me"),
  updateStatus: (status) => api.patch("/auth/status", { status }),
  verifyInvite: (inviteCode) => api.get(`/auth/verify-invite/${inviteCode}`),
  heartbeat: () => api.post("/auth/heartbeat"),
  getOnlineUsers: () => api.get("/auth/online"),
  toggleCallAvailability: () => api.patch("/auth/call-availability"),
  changePassword: (currentPassword, newPassword) => api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword }),
  adminResetPassword: (userId, newPassword) => api.post("/auth/admin/reset-password", { user_id: userId, new_password: newPassword }),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, newPassword) => api.post("/auth/reset-password", { token, new_password: newPassword }),
  updateProfile: (data) => api.patch("/auth/profile", data),
  uploadAvatar: (formData) => api.post("/auth/avatar", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  adminUploadAvatar: (userId, formData) => api.post(`/auth/avatar/${userId}`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  getPresetAvatars: () => api.get("/auth/avatar/presets"),
  setPresetAvatar: (presetUrl, userId) => api.put("/auth/avatar/preset", { preset_url: presetUrl, user_id: userId }),
  refreshToken: () => api.post("/auth/refresh-token"),
  // 2FA
  setup2FA: () => api.post("/auth/2fa/setup"),
  verify2FASetup: (code) => api.post("/auth/2fa/verify-setup", { code }),
  verify2FALogin: (tempToken, code) => api.post("/auth/2fa/verify-login", { temp_token: tempToken, code }),
  verify2FARecovery: (tempToken, code) => api.post("/auth/2fa/verify-recovery", { temp_token: tempToken, code }),
  regenerateRecoveryCodes: (password) => api.post("/auth/2fa/regenerate-recovery", { password }),
  disable2FA: (password) => api.post("/auth/2fa/disable", { password }),
  get2FAStatus: () => api.get("/auth/2fa/status"),
  // Admin
  unlockUser: (userId) => api.post("/auth/admin/unlock-user", { user_id: userId }),
  forceLogout: (userId) => api.post("/auth/admin/force-logout", { user_id: userId }),
  adminDisable2FA: (userId) => api.post("/auth/admin/disable-2fa", { user_id: userId }),
  forceLogoutAll: (confirm) => api.post("/auth/admin/force-logout-all", { confirm }),
  emailHealth: () => api.get("/auth/admin/email-health"),
  getSecurityUsers: () => api.get("/auth/admin/security-users"),
  // Permissions
  getPermissionsMatrix: () => api.get("/auth/permissions/matrix"),
  getUserPermissions: (userId) => api.get(`/auth/users/${userId}/permissions`),
  updateUserPermissions: (userId, customPermissions) => api.put(`/auth/users/${userId}/permissions`, { custom_permissions: customPermissions }),
};

// Contacts API
export const contactsAPI = {
  getAll: () => api.get("/contacts"),
  get: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post("/contacts", data),
  update: (id, data) => api.patch(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  toggleFavorite: (id) => api.patch(`/contacts/${id}/favorite`),
  getByCompany: (companyId, params) => api.get(`/contacts/by-company/${companyId}`, { params }),
  getTags: () => api.get("/contacts/tags"),
  exportCSV: (companyId) => api.get(`/contacts/export/${companyId}`, { responseType: "blob" }),
  importCSV: (companyId, formData, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.dryRun) params.set("dry_run", "true");
    if (opts.onAddressInName) params.set("on_address_in_name", opts.onAddressInName);
    const qs = params.toString();
    return api.post(
      `/contacts/import/${companyId}${qs ? `?${qs}` : ""}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
  },
  getRecentlyContacted: (companyId) => api.get(`/contacts/recently-contacted/${companyId}`),
  getDuplicates: (companyId) => api.get(`/contacts/duplicates/${companyId}`),
  getActivityScores: (companyId) => api.get(`/contacts/activity-scores/${companyId}`),
  // Centralized directory (all types) + provider master-list typeahead
  getDirectory: (params) => api.get("/contacts/directory", { params }),
  searchProviders: (q = "") => api.get("/contacts/providers/search", { params: { q } }),
  backfillDirectory: () => api.post("/contacts/backfill-directory"),
};

// Calls API
export const callsAPI = {
  getHistory: (params) => api.get("/calls", { params }),
  create: (data) => api.post("/calls", data),
  getStats: () => api.get("/calls/stats"),
  getRecent: (limit = 50, mine = false) => api.get("/twilio/recent-calls", { params: { limit, mine } }),
  getMissedCount: () => api.get("/twilio/missed-calls-count"),
  holdCall: (callSid, hold) => api.post("/twilio/hold-call", { call_sid: callSid, hold }),
  // Blind transfer — caller is unilaterally moved to the new destination
  // (phone number or Twilio Client id). Used for business lines, speed
  // dials, and contacts in the Transfer dropdown.
  transferCall: (callSid, transferTo) => api.post("/twilio/transfer-call", { call_sid: callSid, transfer_to: transferTo }),

  // Warm transfer (colleague consultation). The frontend uses this for
  // the Colleagues section of the Transfer dropdown — A holds caller,
  // dials B, consults, then completes or cancels.
  getTransferTargets: () => api.get("/twilio/transfer-targets"),
  warmTransferCall: (callSid, targetUserId, targetNumber) => api.post("/twilio/warm-transfer/initiate", { call_sid: callSid, ...(targetUserId ? { target_user_id: targetUserId } : {}), ...(targetNumber ? { target_number: targetNumber } : {}) }),
  completeTransfer: (callSid) => api.post("/twilio/warm-transfer/complete", { call_sid: callSid }),
  cancelTransfer: (callSid) => api.post("/twilio/warm-transfer/cancel", { call_sid: callSid }),

  // Ring Center advanced controls — merge a second party into the live
  // call as a conference; toggle explicit (operator-owned) recording.
  mergeCall: (callSid, secondParty) => api.post("/twilio/merge-call", { call_sid: callSid, second_party: secondParty }),
  toggleRecording: (callSid, enabled) => api.post("/twilio/toggle-recording", { call_sid: callSid, enabled }),

  // Call Park — drop a caller into a numbered slot (1..5) that any
  // teammate can pick up. Used for "hey, can a Spanish speaker take
  // this?" hand-offs without needing to know who's free upfront.
  parkCall: (callSid, slot) => api.post("/twilio/park-call", { call_sid: callSid, ...(slot ? { slot } : {}) }),
  getParkedCalls: () => api.get("/twilio/parked-calls"),
  getParkAutoReturns: () => api.get("/twilio/park/auto-returns"),
  pickupParkedCall: (slot) => api.post(`/twilio/parked-calls/${slot}/pickup`),
  getParkSlotConfig: () => api.get("/twilio/park-slot-config"),
  setParkSlotConfig: (slot, patch) => api.put(`/twilio/park-slot-config/${slot}`, patch),

  // App ⇄ desk-phone togetherness — mirror & control a call taken on EITHER
  // device. `getMyActiveCall` is polled by the desk-phone companion card.
  getMyActiveCall: () => api.get("/twilio/voice/my-active-call"),
  hangupServerCall: (callSid) => api.post("/twilio/voice/hangup-call", { call_sid: callSid }),
  pullCall: (callSid, toDevice) => api.post("/twilio/voice/pull-call", { call_sid: callSid, to_device: toDevice }),
  // Dispatch "who's on a call" strip (live roster of coworkers on a call).
  getTeamOnCall: () => api.get("/twilio/voice/team-on-call"),
  // Quick post-call note keyed by caller number (lands in caller history).
  addCallNote: (phone, text) => api.post("/ring-center/history/note", { phone, text }),

  // Manually dismiss a call from the "needs a callback" list — used by
  // the Communications workspace inbox "handled" chip. Accepts either
  // the incoming_calls UUID id or the Twilio CallSid.
  markHandled: (callId, handled = true) =>
    api.post(`/twilio/calls/${callId}/mark-handled`, { handled }),
};

// Messages API
export const messagesAPI = {
  getConversations: () => api.get("/messages/conversations"),
  getMessages: (participantId, limit = 100) => 
    api.get(`/messages/${participantId}`, { params: { limit } }),
  send: (data) => api.post("/messages", data),
  markRead: (messageId) => api.patch(`/messages/${messageId}/read`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get("/dashboard/stats"),
  getWeeklyActivity: () => api.get("/dashboard/weekly-activity"),
};

// Twilio API
export const twilioAPI = {
  getToken: (identity, voice = true, video = true) => 
    api.post("/twilio/token", { identity, voice, video }),
  getStatus: () => api.get("/twilio/status"),
  getAvailableNumbers: () => api.get("/twilio/available-numbers"),
  transferCall: (callSid, transferTo, fromNumber) => 
    api.post("/twilio/transfer-call", { call_sid: callSid, transfer_to: transferTo, from_number: fromNumber }),
  getIncomingCallerInfo: (agentCallSid, companyPhone) =>
    api.get("/twilio/incoming-caller-info", { params: { agent_call_sid: agentCallSid, company_phone: companyPhone || "" } }),
  getCurrentCaller: (companyPhone) =>
    api.get("/twilio/current-caller", { params: { company_phone: companyPhone } }),
  getIncomingContext: (callSid) =>
    api.get("/twilio/incoming-context", { params: { call_sid: callSid || "" } }),
  checkAgentCallActive: (agentCallSid) =>
    api.get("/twilio/agent-call-active", { params: { agent_call_sid: agentCallSid } }),
  // Quality telemetry — sent silently when the SDK reports degradation.
  // Backend stores in `call_quality_events` for later pattern analysis.
  reportCallQuality: (body) => api.post("/twilio/call-quality", body),
  // Admin: "why did this call fail?" — surfaces ErrorCode/ErrorMessage
  // captured from Twilio's status webhook (see /voice/status handler).
  diagnoseOutbound: (params = {}) => api.get("/twilio/diagnose-outbound", { params }),
};

// Voicemail API
export const voicemailAPI = {
  getAll: (params) => api.get("/twilio/voicemails", { params }),
  getCount: () => api.get("/twilio/voicemails/count"),
  markRead: (voicemailId) => api.patch(`/twilio/voicemails/${voicemailId}/read`),
  delete: (voicemailId) => api.delete(`/twilio/voicemails/${voicemailId}`),
};

// Speed Dial API
export const speedDialAPI = {
  getAll: () => api.get("/twilio/speed-dials"),
  create: (data) => api.post("/twilio/speed-dials", data),
  delete: (id) => api.delete(`/twilio/speed-dials/${id}`),
};

// Agent Dashboard API
export const agentDashboardAPI = {
  getAgents: () => api.get("/twilio/dashboard/agents"),
  getQueueStats: () => api.get("/twilio/dashboard/queue-stats"),
  getLiveCalls: () => api.get("/twilio/dashboard/live-calls"),
};

// SMS API
export const smsAPI = {
  send: (data) => api.post("/sms/send", data),
  getHistory: (params) => api.get("/sms/history", { params }),
  getConversation: (phoneNumber, ourNumber = null, limit = 100) => 
    api.get(`/sms/conversation/${encodeURIComponent(phoneNumber)}`, { 
      params: { limit, our_number: ourNumber } 
    }),
  getThreads: () => api.get("/sms/threads"),
  searchMessages: (q) => api.get("/sms/search", { params: { q } }),
  getUnreadCounts: () => api.get("/sms/unread-counts"),
  markThreadAsRead: (phoneNumber, ourNumber) => 
    api.post(`/sms/threads/${encodeURIComponent(phoneNumber)}/read`, null, {
      params: { our_number: ourNumber }
    }),
  uploadMedia: (formData) => api.post("/sms/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  }),
  getAvailableNumbers: () => api.get("/sms/available-numbers"),
  // Owner-only destructive ops — require password step-up (soft-delete/archive)
  deleteMessage: (messageId, password) =>
    api.delete(`/sms/messages/${messageId}`, { data: { password } }),
  deleteThread: (phoneNumber, ourNumber, password) =>
    api.delete(`/sms/threads/${encodeURIComponent(phoneNumber)}`, {
      params: { our_number: ourNumber },
      data: { password },
    }),
  getArchived: () => api.get("/sms/archived"),
  restoreThread: (phoneNumber, ourNumber) =>
    api.post("/sms/archived/restore", { phone_number: phoneNumber, our_number: ourNumber }),
  purgeThread: (phoneNumber, ourNumber, password) =>
    api.post("/sms/archived/purge", { phone_number: phoneNumber, our_number: ourNumber, password }),
  // Quick Reply Templates
  getQuickReplies: () => api.get("/sms/quick-replies"),
  createQuickReply: (data) => api.post("/sms/quick-replies", data),
  updateQuickReply: (id, data) => api.patch(`/sms/quick-replies/${id}`, data),
  deleteQuickReply: (id) => api.delete(`/sms/quick-replies/${id}`),
  seedDefaultReplies: () => api.post("/sms/quick-replies/seed-defaults"),
};

// Analytics API
export const analyticsAPI = {
  getOverview: (days = 7) => api.get("/twilio/analytics/overview", { params: { days } }),
  getAgents: (days = 7) => api.get("/twilio/analytics/agents", { params: { days } }),
  getHourly: (days = 7) => api.get("/twilio/analytics/hourly", { params: { days } }),
  getDailyLog: (days = 30) => api.get("/twilio/analytics/daily-log", { params: { days } }),
  sendDailyDigest: (date) => api.post("/twilio/analytics/send-daily-digest", null, { params: date ? { date } : {} }),
  getCalls: (days = 7) => api.get(`/analytics/calls?days=${days}`),
  getAgentWeekly: (weeks = 8, agentId) =>
    api.get("/twilio/analytics/agent-weekly", { params: { weeks, ...(agentId ? { agent_id: agentId } : {}) } }),
};

// Hold Music API
export const holdMusicAPI = {
  upload: (companyId, formData) => api.post(`/twilio/hold-music/upload/${companyId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  }),
  delete: (companyId) => api.delete(`/twilio/hold-music/${companyId}`),
};

// Company Logo API
export const logoAPI = {
  upload: (companyId, formData) => api.post(`/twilio/logo/upload/${companyId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  }),
  delete: (companyId) => api.delete(`/twilio/logo/${companyId}`),
};

// Conference API
export const conferenceAPI = {
  create: (data) => api.post("/twilio/conference/create", data),
  addParticipant: (conferenceId, data) => api.post(`/twilio/conference/${conferenceId}/add-participant`, data),
  getParticipants: (conferenceId) => api.get(`/twilio/conference/${conferenceId}/participants`),
  muteParticipant: (conferenceId, callSid, muted = true) => 
    api.post(`/twilio/conference/${conferenceId}/mute/${callSid}`, null, { params: { muted } }),
  removeParticipant: (conferenceId, callSid) => api.post(`/twilio/conference/${conferenceId}/remove/${callSid}`),
  end: (conferenceId) => api.post(`/twilio/conference/${conferenceId}/end`),
  list: (activeOnly = true) => api.get("/twilio/conferences", { params: { active_only: activeOnly } }),
};

// Scheduled Callbacks API
export const callbacksAPI = {
  getAll: (includePast = false) => api.get("/twilio/scheduled-callbacks", { params: { include_past: includePast } }),
  getUpcoming: () => api.get("/twilio/scheduled-callbacks/upcoming"),
  create: (data) => api.post("/twilio/scheduled-callbacks", data),
  update: (callbackId, status) => api.patch(`/twilio/scheduled-callbacks/${callbackId}`, null, { params: { status } }),
  delete: (callbackId) => api.delete(`/twilio/scheduled-callbacks/${callbackId}`),
};

// Instant Messaging API
export const imAPI = {
  getUsers: () => api.get("/im/users"),
  getConversations: () => api.get("/im/conversations"),
  createConversation: (data) => api.post("/im/conversations", data),
  updateConversation: (id, data) => api.put(`/im/conversations/${id}`, data),
  getMembers: (id) => api.get(`/im/conversations/${id}/members`),
  addMembers: (id, userIds) => api.post(`/im/conversations/${id}/members`, { user_ids: userIds }),
  removeMember: (id, userId) => api.delete(`/im/conversations/${id}/members/${userId}`),
  setMemberHistory: (id, memberId, shareHistory) => api.patch(`/im/conversations/${id}/members/${memberId}/history`, { share_history: shareHistory }),
  getMessages: (conversationId, limit = 200, before = null) => api.get(`/im/conversations/${conversationId}/messages`, { params: before ? { limit, before } : { limit } }),
  getUnreadCount: () => api.get("/im/unread-count"),
  getUnreadMentions: () => api.get("/im/unread-mentions"),
  searchMessages: (q, limit = 30) => api.get("/im/search", { params: { q, limit } }),
  // Team-wide default chat chime — admin uploads via adminAPI, every
  // device fetches via this endpoint. 204 when not configured.
  getTeamDefaultSound: () => api.get("/im/team-default-sound", { validateStatus: (s) => s === 200 || s === 204 }),
};

// Chat API (Web Chat Bot)
export const chatAPI = {
  // Public endpoints (for widget)
  createSession: (companyId, data) => api.post(`/chat/sessions?company_id=${companyId}`, data),
  sendVisitorMessage: (sessionId, data) => api.post(`/chat/sessions/${sessionId}/messages`, data),
  getMessages: (sessionId, limit = 100) => api.get(`/chat/sessions/${sessionId}/messages`, { params: { limit } }),
  transferToAgent: (sessionId, reason = null) => 
    api.post(`/chat/sessions/${sessionId}/transfer`, { reason }),
  getWidgetConfig: (companyId) => api.get(`/chat/widget-config/${companyId}`),
  
  // Authenticated endpoints (for agents)
  getSessions: (params) => api.get("/chat/sessions", { params }),
  sendAgentReply: (sessionId, message, opts = {}) => api.post(`/chat/sessions/${sessionId}/agent-reply`, { message, client_id: opts.client_id }),
  closeSession: (sessionId) => api.post(`/chat/sessions/${sessionId}/close`),
  getUnreadCount: () => api.get("/chat/unread-count"),
};

// Admin API
export const adminAPI = {
  // Users
  getUsers: () => api.get("/admin/users"),
  createUser: (data) => api.post("/admin/users", data),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  resetPassword: (userId, newPassword) => api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword }),
  autoAssignExtensions: () => api.post("/admin/extensions/auto-assign"),
  getNextExtension: () => api.get("/phone/extensions/next"),
  sendUrgentSms: (data) => api.post("/twilio/urgent-message", data),
  
  // Companies
  getCompanies: () => api.get("/admin/companies"),
  createCompany: (data) => api.post("/admin/companies", data),
  updateCompany: (companyId, data) => api.patch(`/admin/companies/${companyId}`, data),
  deleteCompany: (companyId) => api.delete(`/admin/companies/${companyId}`),
  uploadGreeting: (companyId, greetingType, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/twilio/companies/${companyId}/greeting?greeting_type=${greetingType}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  
  // Twilio Numbers
  getNumbers: () => api.get("/admin/twilio-numbers"),
  addNumber: (data) => api.post("/admin/twilio-numbers", data),
  assignNumber: (numberId, userId) => 
    api.patch(`/admin/twilio-numbers/${numberId}/assign`, null, { params: { user_id: userId } }),
  deleteNumber: (numberId) => api.delete(`/admin/twilio-numbers/${numberId}`),
  setDefaultNumber: (numberId) => api.patch(`/admin/twilio-numbers/${numberId}/set-default`),
  
  // Call Routing
  getCallRouting: () => api.get("/admin/call-routing"),
  updateCallRouting: (data) => api.patch("/admin/call-routing", data),

  // Client numbering (AVD-XXXXXX sequence) — super-admin only
  getClientNumbering: () => api.get("/admin/client-numbering"),
  resetClientNumbering: (data) => api.post("/admin/client-numbering/reset", data),

  // Staff numbering (EMP-XXXXXX sequence) — super-admin only
  getStaffNumbering: () => api.get("/admin/staff-numbering"),
  resetStaffNumbering: (data) => api.post("/admin/staff-numbering/reset", data),

  // Fresh Start — guarded bulk wipe (super-admin only)
  freshStartPreview: (scope = "both") => api.get("/admin/fresh-start/preview", { params: { scope } }),
  freshStartExecute: (data) => api.post("/admin/fresh-start/execute", data),
  
  // Invitations
  getInvitations: () => api.get("/admin/invitations"),
  createInvitation: (data) => api.post("/admin/invitations", data),
  deleteInvitation: (invitationId) => api.delete(`/admin/invitations/${invitationId}`),
  resendInvitation: (invitationId) => api.post(`/admin/invitations/${invitationId}/resend`),
  
  // Queue
  getQueueStatus: () => api.get("/twilio/queue/status"),
  answerQueuedCall: (callId) => api.post(`/twilio/queue/answer/${callId}`),

  // Team-wide default Team Chat chime — admin only.
  getTeamChatSound: () => api.get("/admin/team-chat-sound"),
  uploadTeamChatSound: (file) => {
    const form = new FormData();
    form.append("file", file);
    return api.post("/admin/team-chat-sound", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  clearTeamChatSound: () => api.delete("/admin/team-chat-sound"),
};

// SIP Endpoints API
export const sipAPI = {
  getAll: () => api.get("/twilio/sip-endpoints"),
  create: (data) => api.post("/twilio/sip-endpoints", data),
  update: (endpointId, params) => api.put(`/twilio/sip-endpoints/${endpointId}`, null, { params }),
  delete: (endpointId) => api.delete(`/twilio/sip-endpoints/${endpointId}`),
};

// Fax API (HumbleFax)
export const faxAPI = {
  getHealth: () => api.get("/fax/health"),
  getSent: () => api.get("/fax/sent"),
  getIncoming: () => api.get("/fax/incoming"),
  getHistory: () => api.get("/fax/history"),
  send: (formData) => api.post("/fax/send", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  downloadSent: (faxId) => api.get(`/fax/download/sent/${faxId}`),
  downloadIncoming: (faxId) => api.get(`/fax/download/incoming/${faxId}`),
  getTemplates: () => api.get("/fax/templates"),
  createTemplate: (data) => api.post("/fax/templates", data),
  updateTemplate: (id, data) => api.put(`/fax/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/fax/templates/${id}`),
  // Delivery status
  refreshStatus: () => api.post("/fax/refresh-status"),
  refreshSingleStatus: (faxId) => api.post(`/fax/refresh-status/${faxId}`),
  retryFax: (faxId) => api.post(`/fax/retry/${faxId}`),
  getStatusUpdates: () => api.get("/fax/status-updates"),
  getPipelineDeliveries: (pipelineId) => api.get(`/fax/delivery/${pipelineId}`),
  // Address Book — DEPRECATED. Fax address book is now unified with the global
  // `/api/contacts` directory (PhoneBook). Use `contactsAPI` instead.
  // AI client match for incoming fax
  aiMatchIncoming: (humblefaxId) => api.post(`/fax-matcher/ai-match/${humblefaxId}`),
  assignIncoming: (humblefaxId, data) => api.post(`/fax-matcher/assign-incoming/${humblefaxId}`, data),
};

// AI Operator API
export const aiOperatorAPI = {
  getSessions: (limit = 50) => api.get(`/twilio/ai-operator/sessions?limit=${limit}`),
  getCallNotes: (sessionId) => api.get(`/twilio/ai-operator/sessions/${sessionId}/notes`),
  getVoices: () => api.get("/twilio/ai-operator/voices"),
  testVoice: (voiceId, text) => api.post("/twilio/ai-operator/test-voice", { voice_id: voiceId, text }),
  getInbox: (includeHandled = false) => api.get(`/twilio/ai-operator/inbox?include_handled=${includeHandled}`),
  resolveInboxItem: (type, id) => api.post("/twilio/ai-operator/inbox/resolve", { type, id }),
  getSummary: (days = 7) => api.get(`/twilio/ai-operator/summary?days=${days}`),
  getInterpreterRequests: (status = "") => api.get(`/twilio/ai-operator/interpreter-requests${status ? `?status=${status}` : ""}`),
  updateInterpreterRequest: (id, data) => api.patch(`/twilio/ai-operator/interpreter-requests/${id}`, data),
};

// Call Departments API (named transfer destinations -> in-app teammates)
export const departmentsAPI = {
  list: () => api.get("/twilio/departments"),
  create: (data) => api.post("/twilio/departments", data),
  update: (id, data) => api.patch(`/twilio/departments/${id}`, data),
  remove: (id) => api.delete(`/twilio/departments/${id}`),
};

// Video Conferencing API
export const videoAPI = {
  createRoom: (data) => api.post("/video/rooms", data),
  listRooms: () => api.get("/video/rooms"),
  getToken: (data) => api.post("/video/token", data),
  endRoom: (roomId) => api.post(`/video/rooms/${roomId}/end`),
  createInvite: (roomId) => api.post(`/video/rooms/${roomId}/invite`),
  // Invite teammates (already in the app) to an active meeting.
  listTeamUsers: () => api.get("/video/team-users"),
  inviteColleagues: (roomId, data) => api.post(`/video/rooms/${roomId}/invite-colleagues`, data),
  listRoomInvites: (roomId) => api.get(`/video/rooms/${roomId}/invites`),
  markInviteJoined: (roomId) => api.post(`/video/rooms/${roomId}/invites/mark-joined`),
  validateInvite: (code) => api.get(`/video/join/${code}`),
  getGuestToken: (data) => api.post("/video/guest-token", data),
  sendChat: (roomId, data) => api.post(`/video/rooms/${roomId}/chat`, data),
  getChat: (roomId) => api.get(`/video/rooms/${roomId}/chat`),
  scheduleMeeting: (data) => api.post("/video/meetings/schedule", data),
  listScheduled: () => api.get("/video/meetings/scheduled"),
  cancelMeeting: (id) => api.delete(`/video/meetings/${id}`),
  startScheduled: (id) => api.post(`/video/meetings/${id}/start`),
  // Interpretation
  getLanguages: () => api.get("/video/languages"),
  startInterpretation: (roomId, data) => api.post(`/video/rooms/${roomId}/interpretation/start`, data),
  stopInterpretation: (roomId) => api.post(`/video/rooms/${roomId}/interpretation/stop`),
  assignInterpreter: (roomId, data) => api.post(`/video/rooms/${roomId}/interpreters`, data),
  removeInterpreter: (roomId, userId) => api.delete(`/video/rooms/${roomId}/interpreters/${userId}`),
  getInterpreters: (roomId) => api.get(`/video/rooms/${roomId}/interpreters`),
  requestInterpreter: (roomId, data) => api.post(`/video/rooms/${roomId}/interpretation/request`, data),
  listInterpreterRequests: () => api.get("/video/interpretation/requests"),
  updateInterpreterRequest: (id, data) => api.put(`/video/interpretation/requests/${id}`, data),
  // Meeting summaries & transcription
  transcribeMeeting: (roomId) => api.post(`/video/rooms/${roomId}/transcribe`),
  getTranscript: (roomId) => api.get(`/video/rooms/${roomId}/transcript`),
  generateSummary: (roomId) => api.post(`/video/rooms/${roomId}/summary`),
  getSummary: (roomId) => api.get(`/video/rooms/${roomId}/summary`),
};

// Lead Tracker API
export const leadsAPI = {
  list: (params) => api.get("/leads", { params }),
  get: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post("/leads", data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  getFieldOptions: () => api.get("/leads/field-options"),
  updateFieldOptions: (field, options) => api.put(`/leads/field-options/${field}`, { options }),
  stats: () => api.get("/leads/stats/summary"),
  logSms: (leadId, data) => api.post(`/leads/${leadId}/sms-log`, data),
  getActivity: (leadId) => api.get(`/leads/${leadId}/activity`),
  importFile: (formData) => api.post("/leads/import", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  convert: (leadId) => api.post(`/leads/${leadId}/convert-to-client`),
  analytics: (params) => api.get("/leads/stats/analytics", { params }),
};

// Dashboard Shortcuts API
export const shortcutsAPI = {
  list: () => api.get("/shortcuts"),
  create: (data) => api.post("/shortcuts", data),
  update: (id, data) => api.put(`/shortcuts/${id}`, data),
  delete: (id) => api.delete(`/shortcuts/${id}`),
  reorder: (items) => api.put("/shortcuts/reorder/bulk", { items }),
};

// Notepad API
export const notepadAPI = {
  get: () => api.get("/notepad"),
  save: (content) => api.put("/notepad", { content }),
  listNotes: () => api.get("/notepad/notes"),
  createNote: (data) => api.post("/notepad/notes", data),
  updateNote: (noteId, data) => api.put(`/notepad/notes/${noteId}`, data),
  deleteNote: (noteId) => api.delete(`/notepad/notes/${noteId}`),
};


// User Status API
export const userStatusAPI = {
  get: () => api.get("/user-status"),
  set: (message) => api.put("/user-status", { message }),
  getTeam: () => api.get("/user-status/team"),
  react: (userId, emoji) => api.post(`/user-status/${userId}/react`, { emoji }),
};

// Task List API
export const projectsAPI = {
  list: (params) => api.get("/projects", { params }),
  mine: () => api.get("/projects", { params: { mine: true } }),
  myWork: () => api.get("/projects/_/my-work"),
  get: (id) => api.get(`/projects/${id}`),
};

export const tasksAPI = {
  list: (params) => api.get("/tasks", { params }),
  create: (data) => api.post("/tasks", data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  restore: (id) => api.post(`/tasks/${id}/restore`),
  purge: (id) => api.post(`/tasks/${id}/purge`),
  stats: () => api.get("/tasks/stats/summary"),
  counts: () => api.get("/tasks/counts"),
  // Team-wide assignable users (bypasses IM's company scope so
  // cross-company teammates like Dawn/Oscar always show up).
  assignableUsers: () => api.get("/tasks/assignable-users"),
  // Distinct clients currently linked to at least one open task.
  linkedClients: () => api.get("/tasks/linked-clients"),
  // 10 tasks I most recently touched — powers the "My Recent Activity"
  // sidebar for quick jump-back after a distraction.
  myRecentActivity: (limit = 10) => api.get(`/tasks/my-recent-activity?limit=${limit}`),
  // Comments
  // ``mentioned_user_ids`` powers @-mention nudges (send_system_dm)
  // and auto-subscribes each mentioned user as a watcher on the task.
  addComment: (id, body, mentioned_user_ids = []) =>
    api.post(`/tasks/${id}/comments`, { body, mentioned_user_ids }),
  deleteComment: (id, commentId) => api.delete(`/tasks/${id}/comments/${commentId}`),
  // Watchers
  watch: (id) => api.post(`/tasks/${id}/watch`),
  unwatch: (id) => api.delete(`/tasks/${id}/watch`),
  // Bulk
  bulkClose: (ids) => api.post("/tasks/bulk/close", { task_ids: ids }),
  bulkReassign: (ids, assigneeIds) => api.post("/tasks/bulk/reassign", { task_ids: ids, assignees: assigneeIds }),
  bulkDelete: (ids) => api.post("/tasks/bulk/delete", { task_ids: ids }),
  // Attachments
  uploadAttachment: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/tasks/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  deleteAttachment: (id, attId) => api.delete(`/tasks/${id}/attachments/${attId}`),
};

// Task Templates — reusable blueprints (Onboarding, QA, etc.)
export const taskTemplatesAPI = {
  list: () => api.get("/task-templates"),
  create: (data) => api.post("/task-templates", data),
  update: (id, data) => api.put(`/task-templates/${id}`, data),
  delete: (id) => api.delete(`/task-templates/${id}`),
  instantiate: (id, opts) => api.post(`/task-templates/${id}/instantiate`, opts || {}),
};

// SMS Templates — canned text/blast messages. Any authed user can LIST/USE;
// create/update/delete are admin-gated on the backend (403 otherwise).
export const smsTemplatesAPI = {
  list: () => api.get("/sms/templates"),
  create: (data) => api.post("/sms/templates", data),
  update: (id, data) => api.patch(`/sms/templates/${id}`, data),
  delete: (id) => api.delete(`/sms/templates/${id}`),
};

// Chat translation — Emergent LLM (gpt-4o-mini) via universal key.
export const chatTranslateAPI = {
  translate: (text, target = "en") => api.post("/chat/translate", { text, target }),
  translateMessage: (messageId, target) => api.post(`/chat/messages/${messageId}/translate`, target ? { target } : {}),
  getPreferred: () => api.get("/chat/preferred-language"),
  setPreferred: (language) => api.patch("/chat/preferred-language", { language }),
};

// Teams API (super-admin managed groupings, used by Calendar visibility)
export const teamsAPI = {
  list: () => api.get("/teams"),
  myTeams: () => api.get("/teams/me"),
  create: (data) => api.post("/teams", data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  addMember: (id, userId) => api.post(`/teams/${id}/members`, { user_id: userId }),
  removeMember: (id, userId) => api.delete(`/teams/${id}/members/${userId}`),
};

// Contact Notes & History API
export const contactNotesAPI = {
  getNotes: (contactId) => api.get(`/contacts/${contactId}/notes`),
  addNote: (contactId, text) => api.post(`/contacts/${contactId}/notes`, { text }),
  deleteNote: (contactId, noteId) => api.delete(`/contacts/${contactId}/notes/${noteId}`),
  getHistory: (contactId) => api.get(`/contacts/${contactId}/history`),
};

// Document Library API (native MongoDB)
export const docLibraryAPI = {
  listDocuments: (params) => api.get("/doc-library/documents", { params }),
  getDocument: (id) => api.get(`/doc-library/documents/${id}`),
  createDocument: (data) => api.post("/doc-library/documents", data),
  updateDocument: (id, data) => api.put(`/doc-library/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/doc-library/documents/${id}`),
  clientDocAudit: () => api.get("/doc-library/admin/client-doc-audit"),
  purgeClientDocs: () => api.post("/doc-library/admin/purge-client-docs"),
  uploadFile: (formData) => api.post("/doc-library/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  listPackets: () => api.get("/doc-library/packets"),
  getPacket: (id) => api.get(`/doc-library/packets/${id}`),
  // Share links
  generateShareLink: (id) => api.post(`/doc-library/documents/${id}/share`),
  revokeShareLink: (id) => api.delete(`/doc-library/documents/${id}/share`),
  // Submissions
  listSubmissions: (params) => api.get("/doc-library/submissions", { params }),
  getSubmission: (id) => api.get(`/doc-library/submissions/${id}`),
  submissionCount: () => api.get("/doc-library/submissions/count"),
  updateSubmissionStatus: (id, status) => api.put(`/doc-library/submissions/${id}/status`, { status }),
};

// Healthcare Hub API
export const welcomeAPI = {
  acknowledge: (version, surface = "web") =>
    api.post("/welcome/acknowledge", { version, surface }),
};

export const wordProcessorAPI = {
  list: () => api.get("/word-processor/documents"),
  create: (data) => api.post("/word-processor/documents", data),
  duplicate: (id) => api.post(`/word-processor/documents/${id}/duplicate`),
  logCorrespondence: (id, channel, recipient) =>
    api.post(`/word-processor/documents/${id}/log-correspondence`, { channel, recipient }),
  get: (id) => api.get(`/word-processor/documents/${id}`),
  update: (id, data) => api.put(`/word-processor/documents/${id}`, data),
  setShared: (id, shared) => api.patch(`/word-processor/documents/${id}/share`, { shared }),
  remove: (id) => api.delete(`/word-processor/documents/${id}`),
  letterhead: () => api.get("/word-processor/letterhead"),
  saveLetterhead: (data) => api.put("/word-processor/letterhead", data),
  export: (id, fmt) =>
    api.post(`/word-processor/documents/${id}/export`, null, { params: { fmt }, responseType: "blob" }),
  saveToClient: (id, clientId) =>
    api.post(`/word-processor/documents/${id}/save-to-client`, { client_id: clientId }),
  saveToCaregiver: (id, staffId) =>
    api.post(`/word-processor/documents/${id}/save-to-caregiver`, { staff_id: staffId }),
  saveToCharts: (id, clientIds, staffIds) =>
    api.post(`/word-processor/documents/${id}/save-to-charts`, { client_ids: clientIds, staff_ids: staffIds }),
  faxRecipients: (q = "") =>
    api.get("/word-processor/fax-recipients", { params: { q } }),
  templatesList: () => api.get("/word-processor/templates"),
  saveTemplate: (data) => api.post("/word-processor/templates", data),
  updateTemplate: (id, data) => api.put(`/word-processor/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/word-processor/templates/${id}`),
  sendFax: (formData) =>
    api.post("/fax/send", formData, { headers: { "Content-Type": "multipart/form-data" } }),
};

export const healthcareAPI = {
  dashboard: () => api.get("/healthcare/dashboard"),
  clientMetrics: () => api.get("/healthcare/client-metrics"),
  listPatients: (params) => api.get("/healthcare/patients", { params }),
  nextClientNumber: () => api.get("/healthcare/patients/next-client-number"),
  nextEmployeeNumber: () => api.get("/healthcare/staff/next-employee-number"),
  getPatient: (id) => api.get(`/healthcare/patients/${id}`),
  createPatient: (data) => api.post("/healthcare/patients", data),
  updatePatient: (id, data) => api.put(`/healthcare/patients/${id}`, data),
  markPatientLost: (id, payload) => api.post(`/healthcare/patients/${id}/mark-lost`, payload),
  restorePatient: (id) => api.post(`/healthcare/patients/${id}/restore`),
  lostReasonsReport: (params) => api.get("/healthcare/reports/lost-reasons", { params }),
  terminationsReport: (params) => api.get("/healthcare/reports/terminations", { params }),
  deletePatient: (id) => api.delete(`/healthcare/patients/${id}`),
  addVitals: (id, data) => api.post(`/healthcare/patients/${id}/vitals`, data),
  uploadPhoto: (id, formData) => api.post(`/healthcare/patients/${id}/photo`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  listStaff: () => api.get("/healthcare/staff"),
  staffDocuments: (staffId) => api.get(`/healthcare/staff/${staffId}/documents`),
  createStaff: (data) => api.post("/healthcare/staff", data),
  listVisits: (params) => api.get("/healthcare/visits", { params }),
  createVisit: (data) => api.post("/healthcare/visits", data),
  auditLog: (limit) => api.get("/healthcare/audit-log", { params: { limit } }),
  seed: () => api.post("/healthcare/seed"),
};

// Central Audit Log API
export const auditAPI = {
  getLogs: (params) => api.get("/audit/logs", { params }),
  getCategories: () => api.get("/audit/categories"),
  getActions: (category) => api.get("/audit/actions", { params: { category } }),
  getStats: () => api.get("/audit/stats"),
};

// Shared API (Cross-App)
export const fieldVisitAPI = {
  list: (params) => api.get("/field-visits", { params }),
  create: (data) => api.post("/field-visits", data),
  get: (id) => api.get(`/field-visits/${id}`),
  assignPacket: (id) => api.post(`/field-visits/${id}/assign-packet`),
  submit: (id, data) => api.post(`/field-visits/${id}/submit`, data || {}),
  emailSignedCopies: (id, data) => api.post(`/field-visits/${id}/email-signed-copies`, data || {}),
  remove: (id) => api.delete(`/field-visits/${id}`),
  // ── Wizard v2 (Phase 1 backend) ──────────────────────────────────────
  patchState: (id, data) => api.patch(`/field-visits/${id}/state`, data),
  progress:   (id) => api.get(`/field-visits/${id}/progress`),
  clearSignature: (id, slot) => api.delete(`/field-visits/${id}/signature/${slot}`),
  // ── Wizard v2 · Phase 3 finalize + downloads ─────────────────────────
  finalize:      (id, opts) => api.post(`/field-visits/${id}/finalize`, opts || {}),
  finalizedList: (id) => api.get(`/field-visits/${id}/finalized`),
  // Field-visit finalize / download endpoints (bearer-auth, browser
  // fetches with the header stored in localStorage.token).
  finalizedPdfUrl: (id, docId) => `${api.defaults.baseURL}/field-visits/${id}/finalized/${docId}`,
  zipUrl:          (id) => `${api.defaults.baseURL}/field-visits/${id}/download-zip`,
  // Reopen a finalized visit for edits — snapshots the old finalize into
  // previous_finalizations so the audit trail survives.
  reopen:          (id, reason) => api.post(`/field-visits/${id}/reopen`, { reason: reason || "" }),
  // Admin-only: seed 7 demo field visits (one at each wizard step)
  seedDemo:        () => api.post("/field-visits/seed-demo?confirm=true"),
  // Canonical legal-text bodies for the "read & review" scroll boxes.
  legalText:       () => api.get("/field-visits/legal-text"),
};

export const signaturesAPI = {
  getClientDocs: (clientId) => api.get(`/signatures/documents/client/${clientId}`),
  sign: (docId, data) => api.post(`/signatures/documents/${docId}/sign`, data),
  downloadPdf: (docId) => api.get(`/signatures/documents/${docId}/pdf`, { responseType: "blob" }),
  // Absolute URL used by <Fvv2PdfViewerModal> so it can fetch the PDF via
  // an auth-bearing browser fetch and render it inline in an iframe.
  pdfUrl: (docId) => `${api.defaults.baseURL}/signatures/documents/${docId}/pdf`,
  savedSignature: () => api.get("/signatures/saved-signature"),
  saveSignature: (data) => api.post("/signatures/saved-signature", data),
};

export const form485API = {
  get: (patientId) => api.get(`/healthcare/patients/${patientId}/485-poc`),
  save: (patientId, data) => api.put(`/healthcare/patients/${patientId}/485-poc`, { data }),
  generate: (patientId, data, fileToEhr = true) =>
    api.post(`/healthcare/patients/${patientId}/485-poc/generate`, { data, file_to_ehr: fileToEhr }),
  download: (patientId, recordId) => api.get(`/healthcare/patients/${patientId}/485-poc/download/${recordId}`, { responseType: "blob" }),
  paStatus: (patientId) => api.get(`/healthcare/patients/${patientId}/pa-packet/status`),
  paDownload: (patientId) => api.get(`/healthcare/patients/${patientId}/pa-packet/download`, { responseType: "blob" }),
  prefill: (patientId) => api.post(`/healthcare/patients/${patientId}/485-poc/prefill`),
};

// 487 + F-11136 PA addenda (form = "487" | "f11136")
export const paAddendaAPI = {
  get: (patientId, form) => api.get(`/healthcare/patients/${patientId}/pa-addendum/${form}`),
  save: (patientId, form, data) => api.put(`/healthcare/patients/${patientId}/pa-addendum/${form}`, { data }),
  generate: (patientId, form, data) => api.post(`/healthcare/patients/${patientId}/pa-addendum/${form}/generate`, { data }),
  download: (patientId, form, recordId) => api.get(`/healthcare/patients/${patientId}/pa-addendum/${form}/download/${recordId}`, { responseType: "blob" }),
  prefill: (patientId, form) => api.post(`/healthcare/patients/${patientId}/pa-addendum/${form}/prefill`),
};

// ICD-10 diagnosis code library
export const icd10API = {
  search: (q, limit = 20) => api.get(`/healthcare/icd10/search`, { params: { q, limit } }),
  stats: () => api.get(`/healthcare/icd10/stats`),
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/healthcare/icd10/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
};

export const sharedAPI = {
  uploadDocument: (formData) => api.post("/shared/documents/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  storeBytes: (data) => api.post("/shared/documents/store-bytes", data),
  downloadDocument: (docId) => api.get(`/shared/documents/${docId}/download`, { responseType: "blob" }),
  listDocuments: (params) => api.get("/shared/documents", { params }),
  deleteDocument: (docId) => api.delete(`/shared/documents/${docId}`),
  // Patient Lookup
  lookupPatients: (q) => api.get("/shared/patients/lookup", { params: { q } }),
  getPatientSummary: (patientId) => api.get(`/shared/patients/${patientId}/summary`),
  // Auth
  validateToken: (token) => api.post("/shared/auth/validate-token", { token }),
  // Staff
  lookupStaff: (q, role) => api.get("/shared/staff/lookup", { params: { q, role } }),
};

export const googleAPI = {
  status: () => api.get("/google/oauth/status"),
  login: () => api.get("/google/oauth/login"),
  disconnect: () => api.post("/google/oauth/disconnect"),
  test: () => api.get("/google/oauth/test"),
  sync485Ranges: () => api.post("/google/oauth/sync-485-named-ranges"),
};

export default api;
